import React, { useState, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/axiosInstance";

import "react-toastify/dist/ReactToastify.css";

const Authentication = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // 1. ปรับการเก็บข้อมูล (otp_pass เปลี่ยนจาก String ธรรมดา เป็น Array 6 ช่อง)
    const [formData, setFormData] = useState({
        user_name: "",
        otp_user: "",
    });
    const [otpArray, setOtpArray] = useState(new Array(6).fill("")); // [ "", "", "", "", "", "" ]

    // ใช้สร้าง Reference เพื่อสั่งให้ cursor กระโดดไปช่องถัดไปอัตโนมัติ
    const inputRefs = useRef([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // จัดการการพิมพ์ในช่องสี่เหลี่ยม OTP ทีละช่อง
    const handleOtpChange = (element, index) => {
        const value = element.value.replace(/[^0-9]/g, ""); // รับเฉพาะตัวเลขเท่านั้น
        if (!value) return;

        const newOtp = [...otpArray];
        newOtp[index] = value.substring(value.length - 1); // เอาเฉพาะตัวล่าสุดตัวเดียว
        setOtpArray(newOtp);

        // ถ้าพิมพ์ช่องนี้เสร็จ และมีช่องถัดไป -> สั่งให้กระโดดไปช่องถัดไปอัตโนมัติ
        if (newOtp[index] && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    // จัดการเมื่อผู้ใช้กดปุ่มลบ (Backspace)
    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            const newOtp = [...otpArray];

            if (!newOtp[index] && index > 0) {
                // ถ้าช่องปัจจุบันว่างอยู่แล้วกดลบ -> ให้ย้อนไปลบช่องก่อนหน้า และย้ายโฟกัสย้อนกลับ
                newOtp[index - 1] = "";
                setOtpArray(newOtp);
                inputRefs.current[index - 1].focus();
            } else {
                // ถ้าช่องปัจจุบันมีค่า -> ลบค่าช่องปัจจุบัน
                newOtp[index] = "";
                setOtpArray(newOtp);
            }
        }
    };

    // รองรับการก๊อปปี้รหัส 6 หลักมาวางลงช่องแรกทีเดียว (Paste)
    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").substring(0, 6);
        if (pasteData.length === 6) {
            const newOtp = pasteData.split("");
            setOtpArray(newOtp);
            inputRefs.current[5].focus(); // วิ่งไปโฟกัสช่องสุดท้าย
        }
    };

    const handleLoginOTP = async () => {
        const finalOtpPass = otpArray.join(""); // รวมร่างสี่เหลี่ยม 6 ช่องกลับมาเป็นสตริงยาว 6 หลัก

        // 2. ตรวจสอบข้อมูลก่อนส่ง
        if (!formData.user_name || !formData.otp_user || finalOtpPass.length !== 6) {
            toast.error("กรุณากรอกข้อมูลและรหัส OTP 6 หลักให้ครบถ้วน");
            return;
        }

        setLoading(true);
        try {
            // 3. ยิงเข้าสู่ระบบ (ส่ง 3 ค่าเท่าเดิม หลังบ้านทำงานเหมือนเดิมไม่ต้องแก้แล้ว)
            const response = await api.post("/api/auth/login-otp", {
                db_user: formData.user_name,
                otp_user: formData.otp_user,
                otp_pass: finalOtpPass
            });

            if (response.data && response.data.success) {
                toast.success("เข้าสู่ระบบด้วย OTP สำเร็จ!");
                if (response.data.token) {
                    localStorage.setItem("token", response.data.token);
                    if (response.data.user) {
                        localStorage.setItem("user", JSON.stringify(response.data.user));
                    }
                }
                navigate("/home");
            }
        } catch (error) {
            const errMsg = error.response?.data?.message || "เกิดข้อผิดพลาดในการเข้าสู่ระบบ";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    backgroundColor: "#80BDB6",
                    position: "relative",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden"
                }}
            >
                {/* Bottom Left */}
                <Box sx={{ position: "absolute", bottom: 16, left: 16 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                        กองควบคุมมาตรฐานซอฟต์แวร์ ศซว.ทอ.
                    </Typography>
                </Box>

                {/* Login Paper Card */}
                <Paper
                    elevation={4}
                    sx={{
                        padding: 4,
                        width: { xs: "90%", sm: "65%", md: "45%", lg: "35%" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        borderRadius: "20px",
                        backgroundColor: "#C8EAE6",
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <Typography variant="h5" fontWeight="bold" color="#1F534D">
                            OTP Login
                        </Typography>
                    </Box>

                    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 2.5 }}>
                        <TextField
                            variant="outlined"
                            label="Username ของระบบ"
                            name="user_name"
                            value={formData.user_name}
                            onChange={handleChange}
                            fullWidth
                            sx={{
                                width: "80%",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "20px",
                                    backgroundColor: "#fff",
                                },
                            }}
                        />

                        <TextField
                            variant="outlined"
                            label="RTAF MAIL (ไม่ต้องมี @rtaf.mi.th)"
                            name="otp_user"
                            value={formData.otp_user}
                            onChange={handleChange}
                            fullWidth
                            sx={{
                                width: "80%",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "20px",
                                    backgroundColor: "#fff",
                                },
                            }}
                        />

                        {/* ข้อความกำกับส่วนกรอก OTP */}
                        <Typography variant="body2" fontWeight="500" color="#1F534D" sx={{ alignSelf: "flex-start", ml: "10%", mt: 1 }}>
                            กรอกรหัส OTP 6 หลัก
                        </Typography>

                        {/* 🌟 โซนสี่เหลี่ยมแยกทีละช่องรหัส 6 ช่อง */}
                        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", width: "80%" }}>
                            {otpArray.map((data, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={data}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    onChange={(e) => handleOtpChange(e.target, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onPaste={index === 0 ? handlePaste : null}
                                    style={{
                                        width: "45px",
                                        height: "45px",
                                        borderRadius: "10px",
                                        border: "1px solid #ccc",
                                        textAlign: "center",
                                        fontSize: "1.2rem",
                                        fontWeight: "bold",
                                        backgroundColor: "#fff",
                                        outline: "none",
                                        color: "#1F534D",
                                        boxShadow: "0px 2px 4px rgba(0,0,0,0.05)"
                                    }}
                                    onFocus={(e) => e.target.select()} // พอโฟกัสแล้วให้คลุมดำตัวเลขเดิมทันที จะได้พิมพ์ทับได้ง่าย
                                />
                            ))}
                        </Box>

                    </Box>

                    {/* Action Buttons Area */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto 1fr",
                            alignItems: "center",
                            width: "80%",
                            mt: 1
                        }}
                    >
                        <div />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Button
                                variant="contained"
                                disabled={loading}
                                onClick={handleLoginOTP}
                                sx={{
                                    borderRadius: "20px",
                                    backgroundColor: "#368D83",
                                    color: "#fff",
                                    px: 3,
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                        backgroundColor: "#286961"
                                    }
                                }}
                            >
                                {loading ? "กำลังตรวจสอบ..." : "ดำเนินการเข้าสู่ระบบด้วย OTP"}
                            </Button>
                            <Button
                                variant="text"
                                onClick={() =>
                                    navigate("/login", {
                                        state: { user_name: formData.user_name },
                                    })
                                }
                                sx={{
                                    borderRadius: "20px",
                                    justifySelf: "flex-end",
                                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                                    "&:hover": {
                                        borderColor: "#286961",
                                        color: "#286961",
                                        backgroundColor: "rgba(54, 141, 131, 0.08)",
                                    },
                                    color: "#368D83",
                                    borderColor: "#368D83",
                                    fontWeight: "500",
                                    fontSize: "0.875rem",
                                    whiteSpace: "nowrap",
                                    gap: 1.5,
                                    mt: 2,
                                    "&:hover": {
                                        backgroundColor: "#286961"
                                    }

                                }}
                            >
                                ย้อนกลับ
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default Authentication;